/**
 * @feature quote-requests
 * @layer presentation/components
 *
 * ConstructionInfoSection - 공사 상세 정보
 * Displays construction type, space type, and construction area (construction category only)
 */

import { type FC } from "react";
import { Building2 } from "@semo/icons";
import { cn } from "../../../utils/cn";
import type {
  QuoteReviewSectionProps,
  ConstructionTypeLabelValue,
  SpaceTypeLabelValue,
  ConstructionAreaLabelValue,
} from "../types";

/**
 * Construction Info Section Component
 * Shows construction-specific details (construction category only)
 */
export const ConstructionInfoSection: FC<QuoteReviewSectionProps> = ({
  data,
  onEdit,
}) => {
  // Extract construction data
  const isValidItem = <T,>(item: unknown): item is T =>
    typeof item === "object" && item !== null;

  const constructionType = isValidItem<ConstructionTypeLabelValue>(
    data.constructionType,
  )
    ? data.constructionType
    : undefined;
  const spaceType = isValidItem<SpaceTypeLabelValue>(data.spaceType)
    ? data.spaceType
    : undefined;
  const constructionArea = isValidItem<ConstructionAreaLabelValue>(
    data.constructionArea,
  )
    ? data.constructionArea
    : undefined;

  // Don't render if no construction data
  if (!constructionType && !spaceType && !constructionArea) {
    return null;
  }

  return (
    <div className="bg-white rounded-[20px] p-6 relative overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 relative z-10">
        <div className="flex items-center gap-3">
          {/* Icon */}
          <div className="w-10 h-10 rounded-[12px] bg-primary/5 flex items-center justify-center text-primary">
            <Building2 className="w-5 h-5" />
          </div>
          {/* Title */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              공사 상세 정보
            </h3>
            <p className="text-sm text-gray-500 font-medium tracking-tight">
              공사 유형 및 사양
            </p>
          </div>
        </div>

        {/* Edit button - optional */}
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className={cn(
              "px-4 py-1.5",
              "text-gray-400",
              "rounded-[10px]",
              "hover:bg-gray-50 hover:text-primary",
              "transition-all",
              "text-sm font-semibold",
            )}
          >
            수정
          </button>
        )}
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 relative z-10">
        {/* Construction Type */}
        {constructionType && (
          <div className="bg-gray-50/50 rounded-[16px] p-4">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
              공사 유형
            </span>
            <span className="text-base font-semibold text-gray-900 tracking-tight">
              {typeof constructionType === "string"
                ? constructionType
                : constructionType.label || constructionType.value}
            </span>
          </div>
        )}

        {/* Space Type */}
        {spaceType && (
          <div className="bg-gray-50/50 rounded-[16px] p-4">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
              공간 종류
            </span>
            <span className="text-base font-semibold text-gray-900 tracking-tight">
              {typeof spaceType === "string"
                ? spaceType
                : spaceType.label || spaceType.value}
            </span>
          </div>
        )}

        {/* Construction Area */}
        {constructionArea && (
          <div className="bg-gray-50/50 rounded-[16px] p-4 col-span-1 sm:col-span-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
              시공 평수
            </span>
            <span className="text-base font-semibold text-gray-900 tracking-tight">
              {typeof constructionArea === "string"
                ? constructionArea
                : constructionArea.label || constructionArea.value}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
